import { memo, useEffect, useState } from "react";
import { Button, Card, HStack, IconButton, Input, Stack, Text, Textarea } from "@chakra-ui/react";
import WithLoginPage from "@/components/withlogin";
import ProfileService from "services/profile";
import { useApp } from "contexts/app";
import { useNavigate } from "react-router";
import { LuPencil, LuPlus, LuStar, LuTrash2 } from "react-icons/lu";
import BottomSheet from "components/bottom-sheet";

const service = new ProfileService();
const createAddressItem = (seed = {}) => ({
    id: seed?.id || `shipping_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
    full_name: seed?.full_name || "",
    phone_number: seed?.phone_number || "",
    address: seed?.address || "",
    is_default: !!seed?.is_default,
});

const createEditorState = (seed = {}) => ({
    id: seed?.id || null,
    full_name: seed?.full_name || "",
    phone_number: seed?.phone_number || "",
    address: seed?.address || "",
    is_default: !!seed?.is_default,
});

const ProfileEditPage = () => {
    const { setPageTitle, setProcessing, showToast } = useApp();
    const navigate = useNavigate();

    const [form, setForm] = useState({
        full_name: "",
        address: "",
        shipping_address_list: [],
    });
    const [editingAddress, setEditingAddress] = useState(null);

    useEffect(() => {
        setPageTitle("Cập nhật thông tin");
        loadData();
    }, []);

    const loadData = async () => {
        setProcessing(true);
        try {
            const data = await service.get();
            const defaultShipping = data?.info?.default_shipping_address || {};
            const shippingAddressList = Array.isArray(data?.info?.shipping_address) ? data.info.shipping_address : [];
            const normalizedShippingList = shippingAddressList.length > 0
                ? shippingAddressList.map((item) => createAddressItem(item))
                : [createAddressItem({
                    full_name: defaultShipping?.full_name || data?.info?.full_name || "",
                    phone_number: defaultShipping?.phone_number || data?.info?.phone || "",
                    address: defaultShipping?.address || data?.info?.address || "",
                    is_default: true,
                })];

            setForm({
                full_name: data?.info?.full_name || "",
                address: data?.info?.address || "",
                shipping_address_list: normalizedShippingList,
            });
        } finally {
            setProcessing(false);
        }
    };

    const setField = (name, value) => {
        setForm((prev) => ({ ...prev, [name]: value }));
    };

    const addShippingAddress = () => {
        setEditingAddress(createEditorState({
            full_name: form.full_name,
            address: form.address,
            is_default: form.shipping_address_list.length === 0,
        }));
    };

    const removeShippingAddress = (id) => {
        setForm((prev) => {
            const nextList = prev.shipping_address_list.filter((item) => item.id !== id);
            if (nextList.length > 0 && !nextList.some((item) => item.is_default)) {
                nextList[0] = { ...nextList[0], is_default: true };
            }

            return {
                ...prev,
                shipping_address_list: nextList,
            };
        });

        setEditingAddress((prev) => prev?.id === id ? null : prev);
    };

    const setDefaultShippingAddress = (id) => {
        setForm((prev) => ({
            ...prev,
            shipping_address_list: prev.shipping_address_list.map((item) => ({ ...item, is_default: item.id === id })),
        }));
    };

    const onEditShippingAddress = (item) => {
        setEditingAddress(createEditorState(item));
    };

    const setEditingField = (name, value) => {
        setEditingAddress((prev) => prev ? { ...prev, [name]: value } : prev);
    };

    const saveEditingAddress = () => {
        if (!editingAddress?.address?.trim()) {
            showToast({ description: "Vui lòng nhập địa chỉ giao hàng", type: "error" });
            return;
        }

        const payload = createAddressItem({
            ...editingAddress,
            full_name: editingAddress.full_name?.trim() || form.full_name,
            phone_number: editingAddress.phone_number?.trim(),
            address: editingAddress.address?.trim(),
            is_default: editingAddress.is_default,
        });

        setForm((prev) => {
            const existed = prev.shipping_address_list.some((item) => item.id === payload.id);
            let nextList = existed
                ? prev.shipping_address_list.map((item) => item.id === payload.id ? payload : item)
                : [...prev.shipping_address_list, payload];

            if (payload.is_default) {
                nextList = nextList.map((item) => ({ ...item, is_default: item.id === payload.id }));
            } else if (nextList.length === 1) {
                nextList = nextList.map((item) => ({ ...item, is_default: true }));
            }

            return {
                ...prev,
                shipping_address_list: nextList,
            };
        });

        setEditingAddress(null);
    };

    const onSave = async () => {
        if (!form.full_name?.trim()) {
            showToast({ description: "Vui lòng nhập họ tên", type: "error" });
            return;
        }

        if (!form.address?.trim()) {
            showToast({ description: "Vui lòng nhập địa chỉ", type: "error" });
            return;
        }

        const shippingAddressList = (form.shipping_address_list || [])
            .map((item) => ({
                id: item.id,
                full_name: item.full_name?.trim(),
                phone_number: item.phone_number?.trim(),
                address: item.address?.trim(),
                is_default: !!item.is_default,
            }))
            .filter((item) => item.address);

        if (shippingAddressList.length > 0 && !shippingAddressList.some((item) => item.is_default)) {
            shippingAddressList[0] = { ...shippingAddressList[0], is_default: true };
        }

        setProcessing(true);
        try {
            await service.updateInfo({
                full_name: form.full_name,
                address: form.address,
                shipping_address_list: shippingAddressList,
            });
            showToast({ description: "Đã cập nhật thông tin", type: "success" });
            navigate("/profile");
        } catch (error) {
            showToast({ description: error?.message || "Cập nhật thất bại", type: "error" });
        } finally {
            setProcessing(false);
        }
    };

    return (
        <div style={{ paddingBottom: 75 }}>
            <Stack gap={3}>
                <Card.Root size="sm">
                    <Card.Header>
                        <Text fontWeight="bold">Thông tin cá nhân</Text>
                    </Card.Header>
                    <Card.Body>
                        <Stack gap={2}>
                            <Text color="fg.muted">Họ tên</Text>
                            <Input value={form.full_name} onChange={(e) => setField("full_name", e.target.value)} />

                            <Text color="fg.muted">Địa chỉ</Text>
                            <Input value={form.address} onChange={(e) => setField("address", e.target.value)} />
                        </Stack>
                    </Card.Body>
                </Card.Root>

                <Card.Root size="sm">
                    <Card.Header>
                        <Text fontWeight="bold">Địa chỉ giao hàng</Text>
                    </Card.Header>
                    <Card.Body>
                        <Stack gap={2}>
                            {form.shipping_address_list.map((item, index) => (
                                <Card.Root key={item.id} size="sm" variant="outline">
                                    <Card.Body>
                                        <HStack gap={3} align="start">
                                            <Stack gap={1} flex={1} minW={0}>
                                                <HStack gap={2}>
                                                    <Text fontWeight="semibold" truncate>{item.full_name || form.full_name || "-"}</Text>
                                                    {item.is_default && (
                                                        <Text color="green.600" fontSize="xs" fontWeight="medium" flexShrink={0}>Mặc định</Text>
                                                    )}
                                                </HStack>
                                                <Text fontSize="sm" color="fg.muted">{item.phone_number || "-"}</Text>
                                                <Text fontSize="sm">{item.address || "-"}</Text>
                                            </Stack>
                                            <HStack gap={1} flexShrink={0}>
                                                <IconButton
                                                    size="sm" variant="ghost"
                                                    colorPalette={item.is_default ? "yellow" : "gray"}
                                                    onClick={() => setDefaultShippingAddress(item.id)}
                                                    title="Đặt làm mặc định"
                                                >
                                                    <LuStar />
                                                </IconButton>
                                                <IconButton size="sm" variant="ghost" onClick={() => onEditShippingAddress(item)} title="Chỉnh sửa">
                                                    <LuPencil />
                                                </IconButton>
                                                <IconButton size="sm" variant="ghost" colorPalette="red" onClick={() => removeShippingAddress(item.id)} title="Xóa">
                                                    <LuTrash2 />
                                                </IconButton>
                                            </HStack>
                                        </HStack>
                                    </Card.Body>
                                </Card.Root>
                            ))}

                            <Button variant="outline" onClick={addShippingAddress}>
                                <LuPlus />
                                Thêm địa chỉ giao hàng
                            </Button>
                        </Stack>

                        <BottomSheet
                            open={!!editingAddress}
                            onOpenChange={(open) => { if (!open) setEditingAddress(null); }}
                            title={editingAddress?.id ? "Chỉnh sửa địa chỉ" : "Thêm địa chỉ mới"}
                        >
                            {editingAddress && (
                                <Stack gap={3} pb={4}>
                                    <Stack gap={1}>
                                        <Text color="fg.muted" fontSize="sm">Người nhận</Text>
                                        <Input value={editingAddress.full_name} onChange={(e) => setEditingField("full_name", e.target.value)} />
                                    </Stack>
                                    <Stack gap={1}>
                                        <Text color="fg.muted" fontSize="sm">Số điện thoại nhận hàng</Text>
                                        <Input value={editingAddress.phone_number} onChange={(e) => setEditingField("phone_number", e.target.value)} />
                                    </Stack>
                                    <Stack gap={1}>
                                        <Text color="fg.muted" fontSize="sm">Địa chỉ giao hàng</Text>
                                        <Textarea value={editingAddress.address} onChange={(e) => setEditingField("address", e.target.value)} rows={3} />
                                    </Stack>
                                    <Button onClick={saveEditingAddress}>Lưu địa chỉ</Button>
                                </Stack>
                            )}
                        </BottomSheet>
                    </Card.Body>
                </Card.Root>

                <Button onClick={onSave}>Lưu thông tin</Button>
            </Stack>
        </div>
    );
};

const withLogin = () => (<WithLoginPage><ProfileEditPage /></WithLoginPage>);

export default memo(withLogin);